import { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { type CastingActor, type SelectedCasting, type SelectedSeedancePresetActor, type SeedancePresetActorItem } from '../state/useGenerationState';
import type { GenerationAsset } from '../state/useGenerationState';
import type { AudioReferenceUsage, AudioVoiceBinding, AudioVoiceBindingKind, VideoReferenceMode } from '../state/types';
import { useGenerationAssets, type AssetsContextValue } from '../state/assetsContext';
import { useProductImageCompletion } from './useProductImageCompletion';
import { useGenerationSettings } from '../state/settingsContext';
import { AudioCatalogPickerModal } from './AudioCatalogPickerModal';
import { buildMentionBadgeMap, buildMentionReferenceLimitContext, filterActorSourcesWithinReferenceLimit, filterAssetsWithinReferenceLimit, filterMentionActorSources, isReferenceIndexDisabled, type ActorMentionGateContext, type MentionActorSource } from '../state/mentionUtils';
import { isBelowMinReferenceSize, AUDIO_USAGE_LABELS, AUDIO_USAGE_OPTIONS } from '../state/assetMediaUtils';
import { useGenerationJobsSafe } from '../state/jobsContext';
import { stripUrlQuery } from '../scenario/utils';
import { SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR } from '../scenario/constants';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { useMentionUiStore } from '../../../stores/mentionUiStore';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useToast } from '../../../hooks/useToast';
import { isCharacterActor } from '../../../features/actors/actorAttributes';
import { useUploadTemporaryMaterial } from '../../../features/materials/hooks';
import { fetchTemporaryMaterialById, fetchCloudMaterialById, extractVideoFrameMaterial, sanitizeMaterialFacePrivacy, type FacePrivacySanitizeStyle } from '../../../features/materials/api';
import { resolveUploadedGenerationAsset } from '../../../features/materials/uploadedAsset';
import { rasterErrorMessage, useEnsureRasterAsset } from '../../../features/materials/ensureRasterAsset';
import { ensureProductLinkFlag } from '../../../features/materials/productLinkFlag';
import { isSvgFile, isUploadableMaterialFile, SVG_LAYER_LOADING_LABEL, svgUploadPrecheckError } from '../../../features/materials/svgLayerUpload';
import { useSvgLayerUploadGate } from '../../../features/materials/useSvgLayerUploadAllowed';
import { isCheckedLayerStack, layerCountLabel, parseLayerStack } from '../../../features/motion/layerStack';
import { MaterialPreviewModal } from '../../materials/shared/MaterialPreviewModal';
import type { MaterialData } from '../../materials/shared/types';
import { usePortalDropdown } from '../scenario/hooks/usePortalDropdown';
import { AlertTriangle, PenTool, VenetianMask } from 'lucide-react';
import { brandConfig } from '../../../config/brands';
import styles from '../GenerationWorkspace.module.css';
import ddStyles from '../scenario/ScenarioGenerator.module.css';

// 動画チップのバッジに出す参考素材モードのラベル
const VIDEO_REFERENCE_MODE_LABELS: Record<VideoReferenceMode, string> = {
  video: '動画',
  first_frame: '最初のフレーム',
  last_frame: '最後のフレーム',
};

// ドロップダウンメニューの選択肢 (バッジと違い「動画」はメニュー内では説明的に)
const VIDEO_REFERENCE_MODE_OPTIONS: Array<{ value: VideoReferenceMode; label: string }> = [
  { value: 'first_frame', label: '最初のフレーム' },
  { value: 'last_frame', label: '最後のフレーム' },
  { value: 'video', label: '動画そのまま' },
];

// usePortalDropdown の外側クリック判定用 (CSS module ではなく素のクラス名)
const VIDEO_REF_MODE_TRIGGER_CLASS = 'videoRefModeTrigger';

// 参照音声チップの用途ラベル/選択肢は assetMediaUtils.ts に一本化 (AudioCatalogPickerModal
// と共有)。「未指定」は存在しない方針のため、推定できない間はラベルではなく警告表示にする。

// 声用途選択時の期待値注記 (§2.4)。添付した録音がそのまま音声トラックに載る保証は
// なく、モデルが声質・話し方の参照として解釈し直す (計画書 §1.3 共通の含意)。
const AUDIO_VOICE_EXPECTATION_NOTE = '声の質感の参照に使われます（録音そのものが再生されるとは限りません）';

const AUDIO_USAGE_TRIGGER_CLASS = 'audioUsageTrigger';
const AUDIO_VOICE_BINDING_TRIGGER_CLASS = 'audioVoiceBindingTrigger';

// GenerationAsset → MaterialData 変換
function toMaterialData(asset: GenerationAsset): MaterialData {
  const base = {
    id: asset.id,
    thumbnail_url: asset.thumbnailUrl,
    thumbnailUrl: asset.thumbnailUrl,
    source_url: asset.sourceUrl,
    sourceUrl: asset.sourceUrl,
    public_url: asset.sourceUrl || asset.thumbnailUrl,
    storage_bucket: asset.storageBucket,
    storage_path: asset.storagePath,
    content_type: asset.contentType,
    asin: asset.asin,
    width: asset.width,
    height: asset.height,
    directions: {},
  };
  return base as MaterialData;
}

type Props = {
  assets: AssetsContextValue['selectedAssets'];
  casting?: SelectedCasting;
  additionalCastings?: CastingActor[];
  onCastingRemove?: (index: number) => void;
  /** Seedance プリセットアクター。AI俳優 (casting) と排他で、片方のみが表示される。 */
  seedancePreset?: SelectedSeedancePresetActor;
  additionalSeedancePresets?: SeedancePresetActorItem[];
  onSeedancePresetRemove?: (index: number) => void;
  onClearAll?: () => void;
  onScrollEndChange?: (isAtEnd: boolean) => void;
  /** このインデックス以降の素材をグレーアウト表示する（API送信対象外を示す） */
  disabledFromIndex?: number;
  /** 個別にグレーアウト表示する素材 id（静止画タブの動画/音声素材など、位置に依らない除外用） */
  disabledAssetIds?: string[];
  /** ドラッグ並べ替えコールバック（設定時のみ並べ替え有効） */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  /** AI俳優の表示位置（0=先頭、N=N番目） */
  castingPosition?: number;
  /** AI俳優の位置変更コールバック */
  onCastingPositionChange?: (position: number) => void;
  /** フレームモード時のオーバーレイ表示（1枚目=最初のフレーム / 2枚目=最後のフレーム） */
  frameModeOverlay?: boolean;
  /** disabledFromIndex を超えた素材がある場合に表示する警告文（省略時は参考画像モード向けのデフォルト） */
  disabledWarningText?: string;
  /** フレームモード時に空きスロットをプレースホルダーとして埋める枚数 */
  framePlaceholderCount?: number;
  /** プレースホルダー内に表示するアスペクト比矩形 */
  framePlaceholderAspectRatio?: '16:9' | '9:16';
  /**
   * 俳優 @メンション候補ゲート (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
   * ScenarioGenerator の mentionActorSources 構築と同じ条件を渡し、ポップアップ候補と
   * このパネルのバッジが食い違わないようにする。省略時はゲートしない (呼び出し側が
   * 俳優機能を使わない画面等)。
   */
  mentionActorGate?: ActorMentionGateContext;
  /**
   * レイヤー素材のモーダル (MotionLayerModal) を開く要求 (motion-graphics-mode-plan.md §5.2)。
   * 渡されたときだけ、レイヤー素材のチップに「N レイヤー」バッジを付け、クリックでこれを呼ぶ。
   * 呼び出し側 (GenerationWorkspace) はモーショングラフィックス中 (isPromptMotionMode) だけ渡す。
   */
  onOpenLayerModal?: (assetId: string) => void;
};

// ドラッグ並べ替え時のドロップ先マーカー（セリフ体「I」型）
function DropMarker({ position }: { position: 'before' | 'after' }) {
  const color = 'rgba(var(--brand-primary-rgb) / 1)';
  const sideStyle = position === 'before'
    ? { left: -3 }
    : { right: -3 };
  const sideStyleBar = position === 'before'
    ? { left: -8 }
    : { right: -8 };
  return (
    <>
      <div style={{ position: 'absolute', top: 0, bottom: 0, width: 3, background: color, zIndex: 3, pointerEvents: 'none', ...sideStyle }} />
      <div style={{ position: 'absolute', top: 0, width: 13, height: 3, background: color, zIndex: 3, pointerEvents: 'none', ...sideStyleBar }} />
      <div style={{ position: 'absolute', bottom: 0, width: 13, height: 3, background: color, zIndex: 3, pointerEvents: 'none', ...sideStyleBar }} />
    </>
  );
}

// 素材プレビューコンポーネント
function AssetPreview({ asset, onClick, bottomLabel, bottomControl, thumbnailOverrideUrl, slotSize, flagged, sanitized, sanitizedLabel, mentionBadge, onOpenLayerModal }: { asset: GenerationAsset; onClick?: () => void; bottomLabel?: string; bottomControl?: React.ReactNode; thumbnailOverrideUrl?: string; slotSize?: { width: number; height: number }; flagged?: boolean; sanitized?: boolean; sanitizedLabel?: string; mentionBadge?: string; onOpenLayerModal?: (assetId: string) => void }) {
  const { removeAsset } = useGenerationAssets();
  // 音声素材の再生 state (素材パネル MaterialCard の narrationPreview と同じ挙動)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeAsset(asset.id);
  };

  const thumbnailUrl = thumbnailOverrideUrl || asset.thumbnailUrl;
  // 音声素材はサムネイル画像を持たないため、素材パネルの音声カードと同じスタイル
  // (波形グレー背景 + 原稿/曲名テキスト + 再生ボタン) で表示する。
  const isAudioAsset = asset.contentType?.startsWith('audio/') ?? false;
  const audioSourceUrl = (asset.sourceUrl ?? '').trim();
  // レイヤー素材 (入稿ゲート通過済み) にだけ「N レイヤー」バッジ。見た目は通常の画像のチップのまま
  const layerStack = onOpenLayerModal ? parseLayerStack(asset.layerStack) : null;
  const layerBadgeStack = layerStack && isCheckedLayerStack(layerStack) ? layerStack : null;

  return (
    <div className={styles.assetPreviewContainer} onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }} draggable={false}>
      <button type="button" className={styles.assetRemove} onClick={handleRemoveClick} aria-label="素材を外す" draggable={false}>
        ✕
      </button>
      <div
        // flagged (実在人物フィルタ対象): サムネイルの角丸 (border-radius) に沿った
        // 内側の赤枠を assetPreview 自体に引く (facePrivacyFlaggedPreview)。
        // 覆面化済み (sanitized) なら対処済みのため赤枠は外す。
        className={`${styles.assetPreview} ${flagged && !sanitized ? ddStyles.facePrivacyFlaggedPreview : ''}`}
        style={slotSize ? { width: slotSize.width, height: slotSize.height } : undefined}
      >
        {isAudioAsset ? (
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px 8px 18px',
              overflow: 'hidden',
              background: "url('/sound_square.png') center / cover no-repeat",
            }}
          >
            <div
              style={{
                fontSize: 9,
                lineHeight: 1.5,
                color: 'rgba(245, 241, 230, 0.7)',
                textAlign: 'center',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-all',
              }}
            >
              {asset.audioText || asset.name || '音声'}
            </div>
            {audioSourceUrl && (
              <>
                <audio
                  ref={audioElRef}
                  src={audioSourceUrl}
                  onEnded={() => setIsAudioPlaying(false)}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const el = audioElRef.current;
                    if (!el) return;
                    if (el.paused) { el.play(); setIsAudioPlaying(true); }
                    else { el.pause(); setIsAudioPlaying(false); }
                  }}
                  aria-label={isAudioPlaying ? '一時停止' : '音声を再生'}
                  style={{
                    position: 'absolute',
                    bottom: 4,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: 'rgba(255, 255, 255, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  {isAudioPlaying ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <polygon points="8,5 19,12 8,19" />
                    </svg>
                  )}
                </button>
              </>
            )}
          </div>
        ) : thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt="選択素材"
            draggable={false}
          />
        ) : (
          <span>No preview</span>
        )}
        {/* 「顔を含む / 覆面化済み・線画化済み」ラベル: overflow:hidden の親に入れて角丸へ自然に沿わせる */}
        {flagged && (
          sanitized
            ? <span className={`${ddStyles.facePrivacyAssetBadge} ${ddStyles.facePrivacyAssetBadgeDone}`}>{sanitizedLabel ?? '覆面化済み'}</span>
            : <span className={ddStyles.facePrivacyAssetBadge}>顔を含む</span>
        )}
        {/* @メンション参照バッジ (docs/features/material-at-mention-plan.md P1)。
            「顔を含む」バナーと同じ左上コーナーを使うため、両者が重ならないよう
            flagged 時はこちらを出さない (顔プライバシー表示を優先)。 */}
        {mentionBadge && !flagged && (
          <span
            className={styles.mentionBadge}
            title={asset.name ? `プロンプトで @ を入力するとこの画像を参照できます（${asset.name}）` : 'プロンプトで @ を入力するとこの画像を参照できます'}
          >
            {mentionBadge}
          </span>
        )}
        {layerBadgeStack && onOpenLayerModal && (
          <button
            type="button"
            className={ddStyles.motionLayerChipBadge}
            data-testid="motion-layer-chip-badge"
            // チップ幅 (約 56px) に収めるため表示はアイコン + 数字だけ。全文は aria-label / title
            aria-label={`${layerCountLabel(layerBadgeStack)} (クリックでレイヤーを確認・編集)`}
            title={`${layerCountLabel(layerBadgeStack)} (クリックでレイヤーを確認・編集)`}
            draggable={false}
            onClick={(e) => {
              e.stopPropagation();
              onOpenLayerModal(asset.id);
            }}
          >
            <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 1.5 14.5 5 8 8.5 1.5 5z" />
              <path d="M1.5 8 8 11.5 14.5 8" />
              <path d="M1.5 11 8 14.5 14.5 11" />
            </svg>
            <span>{layerBadgeStack.layers.length}</span>
          </button>
        )}
        {bottomControl ?? (bottomLabel && (
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '2px 4px',
            background: 'rgba(0, 0, 0, 0.65)',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            textAlign: 'center',
            pointerEvents: 'none',
          }}>
            {bottomLabel}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SelectedAssetsPanel({ assets, casting, additionalCastings = [], onCastingRemove, seedancePreset, additionalSeedancePresets = [], onSeedancePresetRemove, onClearAll, onScrollEndChange, disabledFromIndex, disabledAssetIds, onReorder, castingPosition, onCastingPositionChange, frameModeOverlay, disabledWarningText, framePlaceholderCount, framePlaceholderAspectRatio, mentionActorGate, onOpenLayerModal }: Props) {
  // AI俳優 (casting) と Seedance プリセットアクター (seedancePreset) は排他。複数選択時は
  // selectedCasting/selectedSeedancePresetActor を A、additional* を B,C... として並べる。
  const actorItems: Array<{
    kind: 'casting' | 'seedance-preset';
    /** @メンション解決の正本 (俳優 UUID)。casting/seedance-preset いずれもテーブルの id。 */
    id: string;
    index: number;
    displayName: string;
    imageUrl: string | null | undefined;
    /** casting のみ意味を持つ: CS URL の有無 (@メンション候補ゲート用)。 */
    hasCharacterSheet?: boolean;
    /** casting のみ意味を持つ: キャラクター俳優か (@メンション候補ゲート用。P4/P5)。 */
    isCharacterActor?: boolean;
    onRemove?: (index: number) => void;
  }> = casting
    ? [
        { kind: 'casting', id: casting.id, index: 0, displayName: casting.display_name, imageUrl: casting.face_image_public_url, hasCharacterSheet: !!casting.character_sheet_public_url, isCharacterActor: isCharacterActor(casting), onRemove: onCastingRemove },
        ...additionalCastings.map((item, idx) => ({
          kind: 'casting' as const,
          id: item.id,
          index: idx + 1,
          displayName: item.display_name,
          imageUrl: item.face_image_public_url,
          hasCharacterSheet: !!item.character_sheet_public_url,
          isCharacterActor: isCharacterActor(item),
          onRemove: onCastingRemove,
        })),
      ]
    : seedancePreset
    ? [
        { kind: 'seedance-preset', id: seedancePreset.id, index: 0, displayName: seedancePreset.display_name, imageUrl: seedancePreset.thumbnail_public_url, onRemove: onSeedancePresetRemove },
        ...additionalSeedancePresets.map((item, idx) => ({
          kind: 'seedance-preset' as const,
          id: item.id,
          index: idx + 1,
          displayName: item.display_name,
          imageUrl: item.thumbnail_public_url,
          onRemove: onSeedancePresetRemove,
        })),
      ]
    : [];
  const hasActors = actorItems.length > 0;
  // 俳優 (casting/seedance-preset) の combinedItems 上の挿入位置。isDisabled 判定
  // (下の combinedItems 構築) と @メンションバッジの絞り込みの両方が使うため、
  // レンダー用の combinedItems 構築より前段でも参照できるようここで計算する。
  const effectiveActorPos = castingPosition !== undefined ? Math.min(castingPosition, assets.length) : 0;
  // @メンションバッジ (A, B, C…): 俳優・動画・フレーム素材を除く候補素材のうち、
  // 参照上限でグレーアウトされない素材 (isDisabled が false のもの) にのみ、
  // 表示順 (assets の並び順) で払い出す。上限外の素材は挿入自体はできても実際には
  // 送信されないため、バッジを出さない (docs/features/material-at-mention-plan.md
  // レビュー指摘 P1)。判定条件は combinedItems 構築時の isDisabled (下記) と共有する
  // (filterAssetsWithinReferenceLimit / isReferenceIndexDisabled)。
  // 並び替え・追加削除で文字は変わり得るが、挿入済みトークンは ID で解決されるため
  // 表示上の乖離のみで実害はない (docs/features/material-at-mention-plan.md 参照)。
  // 払い出しロジックは MaterialMentionPopup (プロンプト欄のオートコンプリート) と
  // 共有するため state/mentionUtils.ts に一本化している (両者のバッジ文字を一致させるため)。
  // AI俳優 @メンション候補の静的ゲート (docs/features/material-at-mention-plan.md
  // 「俳優メンション」節・レビュー指摘 P1): actorItems (casting/seedance-preset。排他) を
  // MentionActorSource[] へ変換したのち、mentionActorGate があれば ScenarioGenerator の
  // mentionActorSources と同じ静的条件 (isActorMentionCandidate) で絞り込む。
  const gatedMentionActorSources: MentionActorSource[] = useMemo(() => {
    const sources = actorItems.map((a) => ({
      id: a.id, kind: a.kind, displayName: a.displayName, imageUrl: a.imageUrl,
      hasCharacterSheet: a.hasCharacterSheet,
      isCharacterActor: a.isCharacterActor,
    }));
    return mentionActorGate ? filterMentionActorSources(sources, mentionActorGate) : sources;
  }, [actorItems, mentionActorGate]);
  // Seedance (BytePlus) の参照画像下限 (短辺300px) 未満の素材は実送信時にペイロードから
  // 除外される (useVideoHandlers.ts の buildVideoPayload / handleGenerateSceneVideo)。
  // @メンション候補・参照上限カウントもこの「実送信リスト」を基準に揃えるため、
  // buildMentionReferenceLimitContext / filterAssetsWithinReferenceLimit に渡す前に
  // 事前に除外しておく (除外しないと、実際は送信されない小画像が参照枠を1つ消費した
  // 扱いになり、本来枠内で送信される後続の画像が候補・警告の対象から漏れてしまう)。
  // レンダー用の combinedItems (下記) は引き続き `assets` (グレーアウト表示用に全件必要)
  // をそのまま使う。
  const mentionCandidateAssets = useMemo(() => (
    mentionActorGate?.isSeedanceModel
      ? assets.filter((a) => !isBelowMinReferenceSize(a, SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR))
      : assets
  ), [assets, mentionActorGate?.isSeedanceModel]);
  // @メンション用の参照上限コンテキスト。actorCount は「参照枠を消費する俳優
  // (ゲート通過後の casting のみ)」、effectiveActorPos は画像参照列基準で正規化
  // (buildMentionReferenceLimitContext に一本化。ScenarioGenerator と同一計算)。
  // レンダー用 combinedItems の isDisabled (表示上のグレーアウト) とは別物。
  const mentionLimitContext = useMemo(
    () => buildMentionReferenceLimitContext({
      assets: mentionCandidateAssets,
      gatedActors: gatedMentionActorSources,
      castingPosition,
      disabledFromIndex,
      disabledAssetIds,
    }),
    [mentionCandidateAssets, gatedMentionActorSources, castingPosition, disabledFromIndex, disabledAssetIds],
  );
  const mentionEligibleAssets = useMemo(
    () => filterAssetsWithinReferenceLimit(mentionCandidateAssets, mentionLimitContext),
    [mentionCandidateAssets, mentionLimitContext],
  );
  // CS 方式俳優は参照上限も消費するため、上限で切り捨てられる俳優はバッジ対象から
  // 除く (ポップアップ候補 = ScenarioGenerator の mentionActorSources と同条件)。
  const mentionActorSources: MentionActorSource[] = useMemo(
    () => filterActorSourcesWithinReferenceLimit(gatedMentionActorSources, mentionLimitContext),
    [gatedMentionActorSources, mentionLimitContext],
  );
  // バッジは常時表示せず、いずれかの入力欄で @メンションポップアップが開いている間
  // (=@入力中) だけ表示する。ポップアップの文字と対応が取れるタイミングでのみ
  // 意味を持つ表示のため (開閉は useMaterialMention が mentionUiStore へ通知)。
  const mentionModeActive = useMentionUiStore((s) => s.openPopupCount > 0);
  const mentionBadgeByAssetId = useMemo(
    () => (mentionModeActive
      ? buildMentionBadgeMap(mentionEligibleAssets, mentionActorSources)
      : new Map<string, string>()),
    [mentionModeActive, mentionEligibleAssets, mentionActorSources],
  );
  const { addAsset, updateVideoReferenceMode, updateAudioUsage } = useGenerationAssets();
  // Seedance 実在人物フィルタが指した素材 (useVideoHandlers がジョブ失敗時にセット)。
  // 取得元 URL (署名クエリ除去) で照合し、該当カードを赤枠表示する。
  const jobsState = useGenerationJobsSafe();
  const facePrivacyFlaggedItems = jobsState?.facePrivacyFlagged?.items;
  const facePrivacyFlaggedUrlPaths = useMemo(
    () => new Set(
      (facePrivacyFlaggedItems ?? [])
        .filter((it) => it.url && !it.url.startsWith('asset://'))
        .map((it) => stripUrlQuery(it.url)),
    ),
    [facePrivacyFlaggedItems],
  );
  const isAssetFacePrivacyFlagged = useCallback((asset: GenerationAsset): boolean => {
    if (facePrivacyFlaggedUrlPaths.size === 0) return false;
    const src = stripUrlQuery(asset.sourceUrl);
    const thumb = stripUrlQuery(asset.thumbnailUrl);
    return (!!src && facePrivacyFlaggedUrlPaths.has(src))
      || (!!thumb && facePrivacyFlaggedUrlPaths.has(thumb));
  }, [facePrivacyFlaggedUrlPaths]);
  const toast = useToast();
  // 動画チップの「どこを参考素材に使うか」ドロップダウン。抽出中はチップ単位で表示を変える。
  const videoRefModeDropdown = usePortalDropdown({ triggerClassName: VIDEO_REF_MODE_TRIGGER_CLASS });
  // 音声チップの用途 (声/BGM/効果音/サウンドロゴ) ドロップダウンと、声用途の人物バインド
  // サブメニュー (docs/features/reference-audio-roles-plan.md §2.4)。
  const audioUsageDropdown = usePortalDropdown({ triggerClassName: AUDIO_USAGE_TRIGGER_CLASS });
  const audioVoiceBindingDropdown = usePortalDropdown({ triggerClassName: AUDIO_VOICE_BINDING_TRIGGER_CLASS });
  const [extractingFrameIds, setExtractingFrameIds] = useState<Set<string>>(new Set());
  // サニタイズ (覆面化/線画の静止画作成+永続紐付け) 実行中の素材 ID → スタイル。
  // 動画の再生成は別操作 (従来の生成ボタン) で、以降の Seedance 生成はバックエンドが
  // 自動差し替えする (両スタイル実行済みなら最後に実行した方)。
  const [sanitizingStyleById, setSanitizingStyleById] = useState<Map<string, FacePrivacySanitizeStyle>>(new Map());
  // このセッションで最後に実行したスタイル (バッジの「覆面化済み/線画化済み」出し分け用)。
  // jobsContext の facePrivacySanitizedIds は対処済みフラグとして従来どおり共有する。
  const [sanitizedStyleById, setSanitizedStyleById] = useState<Map<string, FacePrivacySanitizeStyle>>(new Map());
  const handleFacePrivacySanitize = useCallback(async (asset: GenerationAsset, style: FacePrivacySanitizeStyle) => {
    if (sanitizingStyleById.has(asset.id)) return;
    setSanitizingStyleById((prev) => new Map(prev).set(asset.id, style));
    const actionLabel = style === 'line_art' ? '線画化' : '覆面化';
    try {
      await sanitizeMaterialFacePrivacy(asset.type, asset.id, style);
      jobsState?.addFacePrivacySanitizedId(asset.id);
      setSanitizedStyleById((prev) => new Map(prev).set(asset.id, style));
      toast.show(`${style === 'line_art' ? '線画' : '覆面化'}画像を作成しました。動画をもう一度生成すると自動的に差し替えて使用されます`, 10000);
    } catch (err) {
      const serverMessage = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      toast.show(serverMessage || `${actionLabel}に失敗しました。時間をおいて再度お試しください`, 8000);
    } finally {
      setSanitizingStyleById((prev) => {
        const next = new Map(prev);
        next.delete(asset.id);
        return next;
      });
    }
  }, [sanitizingStyleById, jobsState, toast]);
  const openMaterialsPanel = useMaterialsPanelStore((state) => state.open);
  const isPanelOpen = useMaterialsPanelStore((state) => state.isOpen);
  const isMobile = useIsMobile();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // SVG を含むアップロード中は「レイヤーを読み込んでいます」(解析に数秒〜30 秒かかる)
  const [isUploadingSvg, setIsUploadingSvg] = useState(false);
  const uploadMutation = useUploadTemporaryMaterial();
  const ensureRasterAsset = useEnsureRasterAsset();
  // SVG (レイヤー素材) はモーショングラフィックスのタブが見える利用者だけ受ける (ドロップ時に availability を確認)
  const svgGate = useSvgLayerUploadGate();
  // SEカタログピッカー (docs/features/reference-audio-roles-plan.md フェーズ5b・§2.7)。
  // 入口はこのパネル自身に置く (addAsset と同じ「参照音声を選ぶ場所」に揃えるため。
  // 動画素材の参考素材モードドロップダウン等と同じ「チップ周辺に機能を足す」パターン)。
  // 表示条件は addAsset の audioAllowed ゲート (assetsContext.tsx) と同じ:
  // プロンプト入力モード + 参照モード + 対応モデル (maxReferenceAudios>0) のときのみ。
  const { productionMode, promptVideoGenerationMode, maxReferenceAudios } = useGenerationSettings();
  const audioCatalogAvailable = productionMode === 'prompt'
    && promptVideoGenerationMode === 'reference'
    && maxReferenceAudios > 0;
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<GenerationAsset | null>(null);
  // GenerationAsset は生成に必要な最小情報しか持たないため、プレビューでは
  // 素材レコードを取得し直して関連商品・取得元・容量・作成日時等を表示する
  const [previewMaterial, setPreviewMaterial] = useState<MaterialData | null>(null);
  const scrollRef = useRef<HTMLUListElement>(null);
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'before' | 'after'>('before');
  const [hasOverflow, setHasOverflow] = useState(false);
  // 商品に紐づく画像素材のうち選択中素材に無いもの (商品が選択中のときだけ数えられる) を戻す
  const { missingCount: missingProductImageCount, restore: restoreProductImages } = useProductImageCompletion(assets);
  const canRestoreProduct = missingProductImageCount > 0;
  const prevItemRectsRef = useRef<Map<string, DOMRect>>(new Map());

  useEffect(() => {
    if (!previewAsset) {
      setPreviewMaterial(null);
      return;
    }
    let cancelled = false;
    const fetchById = previewAsset.type === 'temporary' ? fetchTemporaryMaterialById : fetchCloudMaterialById;
    fetchById(previewAsset.id)
      .then((fetched) => {
        // 取得できなければ GenerationAsset 由来の簡易データのまま表示する
        if (!cancelled && fetched) setPreviewMaterial(fetched as unknown as MaterialData);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [previewAsset]);

  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setHasOverflow(el.scrollWidth > el.clientWidth);
    if (onScrollEndChange) {
      const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
      onScrollEndChange(isAtEnd);
    }
  }, [onScrollEndChange]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScrollState, { passive: true });
    checkScrollState();
    const observer = new ResizeObserver(checkScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScrollState);
      observer.disconnect();
    };
  }, [checkScrollState, assets.length]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const currentItems = Array.from(el.querySelectorAll<HTMLElement>('[data-drag-item-key]'));
    const currentRects = new Map<string, DOMRect>();
    currentItems.forEach((item) => {
      const key = item.dataset.dragItemKey;
      if (!key) return;
      currentRects.set(key, item.getBoundingClientRect());
    });

    const prevRects = prevItemRectsRef.current;
    currentItems.forEach((item) => {
      const key = item.dataset.dragItemKey;
      if (!key) return;
      const prevRect = prevRects.get(key);
      const nextRect = currentRects.get(key);
      if (!prevRect || !nextRect) return;

      const deltaX = prevRect.left - nextRect.left;
      const deltaY = prevRect.top - nextRect.top;
      if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;

      item.style.transition = 'none';
      item.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      item.style.willChange = 'transform';

      requestAnimationFrame(() => {
        item.style.transition = 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1)';
        item.style.transform = 'translate(0, 0)';
      });

      const clearTransition = () => {
        item.style.transition = '';
        item.style.transform = '';
        item.style.willChange = '';
      };
      item.addEventListener('transitionend', clearTransition, { once: true });
    });

    prevItemRectsRef.current = currentRects;
  }, [assets, actorItems, castingPosition]);

  // 動画チップの参考素材モード切替。フレーム指定は抽出済みキャッシュがあれば即切替、
  // 無ければバックエンドで抽出してから切り替える (失敗時はモードを変えない)。
  const handleSelectVideoRefMode = async (asset: GenerationAsset, mode: VideoReferenceMode) => {
    videoRefModeDropdown.close();
    const current = asset.videoReferenceMode ?? 'video';
    if (mode === current) return;
    if (mode === 'video') {
      updateVideoReferenceMode(asset.id, 'video');
      return;
    }
    const position = mode === 'first_frame' ? 'first' : 'last';
    const cached = asset.videoFrameMaterials?.[position];
    if (cached) {
      updateVideoReferenceMode(asset.id, mode);
      return;
    }
    setExtractingFrameIds((prev) => new Set(prev).add(asset.id));
    try {
      const frame = await extractVideoFrameMaterial(asset.type, asset.id, position);
      const url = frame.preview_url || frame.previewUrl || frame.thumbnail_url || '';
      if (!frame.id || !url) {
        throw new Error('フレーム素材のレスポンスが不正です');
      }
      updateVideoReferenceMode(asset.id, mode, { position, id: frame.id, url });
    } catch (error) {
      console.error('Failed to extract video reference frame:', error);
      toast.show('フレームの抽出に失敗しました');
    } finally {
      setExtractingFrameIds((prev) => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    }
  };

  // 参照音声チップの用途選択。声以外に切り替えたら人物バインドは自動でクリアされる
  // (updateAudioUsage 側)。声を選んだ直後はまだバインド未選択 (ナレーター声) のまま。
  const handleSelectAudioUsage = (asset: GenerationAsset, usage: AudioReferenceUsage) => {
    audioUsageDropdown.close();
    if (usage === asset.audioUsage) return;
    updateAudioUsage(asset.id, usage);
  };

  // 参照音声チップの声の相手 (人物バインド) 選択。undefined=未指定 (ナレーター声)。
  const handleSelectAudioVoiceBinding = (asset: GenerationAsset, binding: AudioVoiceBinding | null) => {
    audioVoiceBindingDropdown.close();
    updateAudioUsage(asset.id, 'voice', binding);
  };

  // 素材が空の時にクリック/タップしたら素材パネルを開く
  const handleEmptyClick = () => {
    // スマホの場合は常に開く、PCの場合は閉じている時だけ開く
    if (isMobile || !isPanelOpen) {
      openMaterialsPanel('manage');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    // 並べ替えドラッグの場合はスキップ（li側で処理済み）
    if (dragFromIndex !== null) return;

    // 1. 既存：素材パネルからのドラッグ（JSON形式）
    let droppedAsset: GenerationAsset | null = null;
    try {
      const dataStr = e.dataTransfer.getData('application/json');
      if (dataStr) {
        droppedAsset = JSON.parse(dataStr) as GenerationAsset;
      }
    } catch (error) {
      console.error('Failed to parse dropped data:', error);
    }
    if (droppedAsset) {
      // 非ラスタ原本 (SVG) でラスタ版が未生成なら取得してから追加する (dragStart は同期なので
      // 受け側で行う: features/materials/ensureRasterAsset.ts)。失敗は理由を出して中断
      try {
        // 手組みのドラッグ payload (シーンフレーム等) は商品紐づきを持たないことがあるので、
        // 1 件取得で asin / hasProductLinks を補ってから追加する (features/materials/productLinkFlag.ts)
        addAsset(await ensureRasterAsset(await ensureProductLinkFlag(droppedAsset)));
      } catch (error) {
        console.error('ラスタ版の取得に失敗:', error);
        toast.error(rasterErrorMessage(error));
      }
      return;
    }

    // 2. ローカルファイルのドロップ → 個人フォルダにアップロード
    const files = Array.from(e.dataTransfer.files);
    // SVG (レイヤー素材) も受ける。受けない利用者の SVG と 16 MB 超はアップロード前に止める
    // (features/materials/svgLayerUpload.ts)。SVG があるときだけ availability を確認する
    const allowSvgUpload = await svgGate.resolveAllowSvg(files);
    const mediaFiles = files.filter((f) => {
      if (!isUploadableMaterialFile(f)) return false;
      const precheck = svgUploadPrecheckError(f, { allowSvg: allowSvgUpload });
      if (precheck) {
        toast.error(`アップロードエラー: ${precheck}`);
        return false;
      }
      return true;
    });

    if (mediaFiles.length === 0) return;

    setIsUploading(true);
    setIsUploadingSvg(mediaFiles.some((f) => isSvgFile(f)));
    try {
      // 並列でアップロード
      const results = await Promise.all(
        mediaFiles.map((file) => uploadMutation.mutateAsync({ file }))
      );
      // アップロード成功後、選択済み素材に追加 (GCS 動画・音声は署名 URL を取得してから。
      // サムネイル JPEG を sourceUrl に代用しない: features/materials/uploadedAsset.ts)
      for (const material of results) {
        try {
          addAsset(await resolveUploadedGenerationAsset(material));
        } catch (error) {
          // 非ラスタ原本 (SVG) のラスタ版が取得できなかった。登録は済んでいるので理由だけ出す
          console.error('ラスタ版の取得に失敗:', error);
          toast.error(rasterErrorMessage(error));
        }
      }
    } catch (error) {
      // エラーはhookのonErrorでtoast表示される
      console.error('File upload failed:', error);
    } finally {
      setIsUploading(false);
      setIsUploadingSvg(false);
    }
  };

  // 素材もキャスティングもない場合（ただしフレームモードのプレースホルダー表示時は通常描画へ進む）
  if (assets.length === 0 && !hasActors && !framePlaceholderCount) {
    const assetLabel = brandConfig.labels.material;
    return (
      <>
        <div
          className={`${styles.selectionList} ${isDragOver ? styles.dragOver : ''} ${styles.selectionListClickable}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={isUploading ? undefined : handleEmptyClick}
          role="button"
          tabIndex={0}
        >
          {isUploading ? (
            <span className={styles.empty}>{isUploadingSvg ? `${SVG_LAYER_LOADING_LABEL}...` : 'アップロード中...'}</span>
          ) : isMobile ? (
            <span className={styles.empty}>
              {isPanelOpen ? `以下の${assetLabel}をタップして選択してください` : `タップして${assetLabel}を追加`}
            </span>
          ) : (
            <div className={styles.emptyDropZone}>
              <span className={styles.empty}>ファイルをドロップ</span>
              <div className={styles.emptyOrDivider}><span>または</span></div>
              <span className={styles.empty}>{`右のパネルから${assetLabel}を追加`}</span>
            </div>
          )}
        </div>
        {/* SEカタログ入口は素材が無い空状態では出さない (裏機能のため。チップ一覧側の
            音符アイコンのみを入口とする)。モーダル自体はピッカーを開いたまま全チップを
            削除した場合の表示継続のため残す。 */}
        <AudioCatalogPickerModal isOpen={catalogPickerOpen} onClose={() => setCatalogPickerOpen(false)} />
      </>
    );
  }

  const totalReferenceItemCount = assets.length + actorItems.length;
  const hasDisabledByIds = !!disabledAssetIds?.length && assets.some((a) => disabledAssetIds.includes(a.id));
  const showRefWarning = (disabledFromIndex !== undefined && totalReferenceItemCount > disabledFromIndex) || hasDisabledByIds;

  // 素材またはキャスティングがある場合
  return (
    <div className={styles.assetGridWrapper}>
    {(brandConfig.id === 'hypeapp' || showRefWarning) && (
      <div style={{ margin: '6px 0px -8px 8px', padding: '0 8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        {brandConfig.id === 'hypeapp' && (
          <span style={{ color: 'rgba(245, 241, 230, 0.5)', fontWeight: 400 }}>
            選択中素材
          </span>
        )}
        {showRefWarning && (
          <span style={{ color: '#f59e0b', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <AlertTriangle size={13} />
            {disabledWarningText ?? (disabledFromIndex !== undefined
              ? `参考画像モードでは先頭から${disabledFromIndex}つの素材のみ利用されます。`
              : '一部の素材は生成に利用されません。')}
          </span>
        )}
      </div>
    )}
    <ul
      ref={scrollRef}
      className={`${styles.assetGrid} ${isDragOver ? styles.dragOver : ''}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {(() => {
        // actor (casting または seedancePreset) と assets を統合した論理リストを構築。
        // 複数 actor は castingPosition から連続して並べる。参照画像 payload も
        // useVideoHandlers 側で同じ位置に全キャラシートを splice する。
        // (effectiveActorPos はコンポーネント上部で計算済み。@メンションバッジの
        // 絞り込みと同じ値を共有する)
        type ActorItem = typeof actorItems[number];
        type CombinedItem = { type: 'actor'; actor: ActorItem } | { type: 'asset'; asset: GenerationAsset; assetIdx: number };
        const combinedItems: CombinedItem[] = [];
        if (hasActors) {
          // assetsの中にactorを挿入
          assets.forEach((asset, idx) => {
            if (idx === effectiveActorPos) {
              actorItems.forEach((actorItem) => combinedItems.push({ type: 'actor', actor: actorItem }));
            }
            combinedItems.push({ type: 'asset', asset, assetIdx: idx });
          });
          if (effectiveActorPos >= assets.length) {
            actorItems.forEach((actorItem) => combinedItems.push({ type: 'actor', actor: actorItem }));
          }
        } else {
          assets.forEach((asset, idx) => combinedItems.push({ type: 'asset', asset, assetIdx: idx }));
        }

        const handleReorder = (fromCombinedIdx: number, toCombinedIdx: number, position: 'before' | 'after') => {
          const fromItem = combinedItems[fromCombinedIdx];
          const toItem = combinedItems[toCombinedIdx];
          if (!fromItem || !toItem) return;

          // 1. combinedItemsの新しい配置を計算（論理的なリスト操作）
          // ドロップ位置: targetの前(before) or 後(after)
          const newCombined = [...combinedItems];
          const [moved] = newCombined.splice(fromCombinedIdx, 1);
          // splice後のtoCombinedIdxを補正
          let insertAt = toCombinedIdx;
          if (fromCombinedIdx < toCombinedIdx) {
            insertAt -= 1; // 前から取り除いたので-1
          }
          if (position === 'after') {
            insertAt += 1;
          }
          insertAt = Math.max(0, Math.min(newCombined.length, insertAt));
          newCombined.splice(insertAt, 0, moved);

          // 同じ並びに戻るドロップは no-op
          const beforeSignature = combinedItems.map((it) => (it.type === 'actor' ? `actor:${it.actor.index}` : `asset:${it.asset.id}`));
          const afterSignature = newCombined.map((it) => (it.type === 'actor' ? `actor:${it.actor.index}` : `asset:${it.asset.id}`));
          if (JSON.stringify(beforeSignature) === JSON.stringify(afterSignature)) return;

          // 2. 新しい配置から actor 位置と assets順序を抽出
          const newAssetOrder: GenerationAsset[] = [];
          let newActorPos = 0;
          for (let i = 0; i < newCombined.length; i++) {
            const it = newCombined[i];
            if (it.type === 'actor') {
              newActorPos = newAssetOrder.length;
            } else {
              newAssetOrder.push(it.asset);
            }
          }

          // 3. actor 位置の更新 (casting / seedancePreset 共通)
          const prevActorPos = effectiveActorPos;
          if (hasActors && newActorPos !== prevActorPos) {
            onCastingPositionChange?.(newActorPos);
          }

          // 4. assets順序の更新（変化があった場合のみ）
          const origAssetIds = assets.map(a => a.id);
          const newAssetIds = newAssetOrder.map(a => a.id);
          if (JSON.stringify(origAssetIds) !== JSON.stringify(newAssetIds)) {
            // 1回のドラッグで動くのは1要素のみ
            if (fromItem.type === 'asset') {
              const oldIdx = fromItem.assetIdx;
              const newIdx = newAssetIds.indexOf(fromItem.asset.id);
              if (newIdx !== -1 && oldIdx !== newIdx) {
                onReorder?.(oldIdx, newIdx);
              }
            }
          }
        };

        return combinedItems.map((item, combinedIdx) => {
          // disabled判定: インデックスベース (参考画像上限等) または id 指定 (静止画タブの動画/音声等)。
          // @メンションバッジの絞り込み (mentionEligibleAssets) と同一条件を共有する
          // (state/mentionUtils.ts の isReferenceIndexDisabled)。
          const isDisabled = isReferenceIndexDisabled(
            combinedIdx,
            item.type === 'asset' ? item.asset.id : undefined,
            { disabledFromIndex, disabledAssetIds },
          );

          if (item.type === 'actor') {
            const actorDraggable = !!onReorder && actorItems.length === 1;
            return (
              <li
                key={`actor-${item.actor.kind}-${item.actor.index}`}
                data-drag-item-key={`actor:${item.actor.index}`}
                className={styles.assetCard}
                style={{
                  position: 'relative',
                  ...(actorDraggable ? { cursor: 'grab' } : undefined),
                }}
                draggable={actorDraggable}
                onDragStart={actorDraggable ? (e) => {
                  setDragFromIndex(combinedIdx);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(combinedIdx));
                } : undefined}
                onDragOver={onReorder ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                  // 要素の中央を境に before / after を判定
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const isAfter = e.clientX > rect.left + rect.width / 2;
                  setDragOverIndex(combinedIdx);
                  setDragOverPosition(isAfter ? 'after' : 'before');
                } : undefined}
                onDragLeave={onReorder ? () => {
                  setDragOverIndex(null);
                } : undefined}
                onDrop={onReorder ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (dragFromIndex !== null && dragFromIndex !== combinedIdx) {
                    handleReorder(dragFromIndex, combinedIdx, dragOverPosition);
                  }
                  setDragFromIndex(null);
                  setDragOverIndex(null);
                } : undefined}
                onDragEnd={onReorder ? () => {
                  setDragFromIndex(null);
                  setDragOverIndex(null);
                } : undefined}
              >
                {dragFromIndex !== null && dragOverIndex === combinedIdx && dragFromIndex !== combinedIdx && (
                  <DropMarker position={dragOverPosition} />
                )}
                {isDisabled && !isMobile && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}>
                    <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>利用されません</span>
                  </div>
                )}
                <div style={{
                  ...(isDisabled ? { opacity: 0.4, filter: 'grayscale(100%)' } : undefined),
                  ...(dragFromIndex === combinedIdx ? { opacity: 0.4 } : undefined),
                }}>
                  <div className={styles.selectedCastingAsset}>
                    <button
                      type="button"
                      className={styles.assetRemove}
                      onClick={() => item.actor.onRemove?.(item.actor.index)}
                      aria-label={`${item.actor.displayName}をクリア`}
                    >
                      ✕
                    </button>
                    <div className={styles.selectedCastingImageRound}>
                      {item.actor.imageUrl ? (
                        <img src={item.actor.imageUrl} alt={item.actor.displayName} draggable={false} />
                      ) : (
                        <div className={styles.selectedCastingPlaceholder} />
                      )}
                    </div>
                    {actorItems.length > 1 && (
                      <span className={styles.selectedCastingOrderBadge}>{String.fromCharCode(65 + item.actor.index)}</span>
                    )}
                    {/* @メンション参照バッジ (docs/features/material-at-mention-plan.md
                        「俳優メンション」節)。素材の mentionBadge と同じ見た目・意味だが、
                        俳優サムネイルは丸型でレイアウトが異なるため左上に個別配置する
                        (selectedCastingOrderBadge が複数俳優時に右上を使うため衝突しない)。 */}
                    {mentionBadgeByAssetId.get(item.actor.id) && (
                      <span
                        className={`${styles.mentionBadge} ${styles.selectedCastingMentionBadge}`}
                        title={`プロンプトで @ を入力するとこの俳優を参照できます（${item.actor.displayName}）`}
                      >
                        {mentionBadgeByAssetId.get(item.actor.id)}
                      </span>
                    )}
                    <span className={styles.selectedCastingLabel}>{item.actor.displayName}</span>
                  </div>
                </div>
              </li>
            );
          }

          // asset
          const facePrivacyFlagged = isAssetFacePrivacyFlagged(item.asset);
          // 対処済み (このセッションで静止画の覆面化/線画化を実行済み)。以降の Seedance
          // 生成はバックエンドが永続紐付けを見て自動差し替えするため、動画の再生成は
          // 従来の生成ボタンで行う。
          const facePrivacySanitized = facePrivacyFlagged
            && (jobsState?.facePrivacySanitizedIds ?? []).includes(item.asset.id);
          const facePrivacySanitizingStyle = sanitizingStyleById.get(item.asset.id);
          // 覆面化/線画化は画像素材のみ (動画・音声は対象外)。
          // 対処済み (facePrivacySanitized) でもボタンは残す: 「覆面化 → 再生成 →
          // まだ弾かれた → 線画化」のフォールバック手順や、スタイルの切り替え
          // (BE は最後に実行したスタイルを採用) を同じカード上で行えるようにする
          // (レビュー指摘 P2)。現在のスタイルはバッジ (覆面化済み/線画化済み) で示す。
          const facePrivacySanitizeAvailable = facePrivacyFlagged
            && item.asset.usage !== 'reference_video'
            && !(item.asset.contentType?.startsWith('audio/') ?? false);
          // 画像・動画素材はシナリオのシーン (冒頭フレーム / 静止画をそのまま使う) へも
          // ドラッグできる。ペイロードは素材パネルと同じ形 (GenerationAsset は buildDragData
          // の出力をそのまま積んだもの)。音声はシーンに置けないため対象外。
          const sceneDraggable = !isMobile
            && !(item.asset.contentType?.startsWith('audio/') ?? false);
          const cardDraggable = !!onReorder || sceneDraggable;
          return (
            <li
              key={item.asset.id}
              data-drag-item-key={`asset:${item.asset.id}`}
              className={styles.assetCard}
              style={{
                position: 'relative',
                ...(cardDraggable ? { cursor: 'grab' } : undefined),
              }}
              draggable={cardDraggable}
              onDragStart={cardDraggable ? (e) => {
                // 並べ替え不可でも記録する: パネル自身の handleDrop が自分発のドラッグを
                // 素材追加として二重処理しないためのガードを兼ねる
                setDragFromIndex(combinedIdx);
                if (onReorder) {
                  e.dataTransfer.setData('text/plain', String(combinedIdx));
                }
                if (sceneDraggable) {
                  e.dataTransfer.setData('application/json', JSON.stringify(item.asset));
                  const isVideo = item.asset.contentType?.startsWith('video/') ?? false;
                  window.dispatchEvent(new CustomEvent('material-drag-start', { detail: { isVideo } }));
                }
                // 並べ替え (move) とシーンへの配置 (copy) の両方を許可する
                e.dataTransfer.effectAllowed = onReorder && sceneDraggable
                  ? 'copyMove'
                  : (onReorder ? 'move' : 'copy');
              } : undefined}
              onDragOver={onReorder ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'move';
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const isAfter = e.clientX > rect.left + rect.width / 2;
                setDragOverIndex(combinedIdx);
                setDragOverPosition(isAfter ? 'after' : 'before');
              } : undefined}
              onDragLeave={onReorder ? () => {
                setDragOverIndex(null);
              } : undefined}
              onDrop={onReorder ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dragFromIndex !== null && dragFromIndex !== combinedIdx) {
                  handleReorder(dragFromIndex, combinedIdx, dragOverPosition);
                }
                setDragFromIndex(null);
                setDragOverIndex(null);
              } : undefined}
              onDragEnd={cardDraggable ? () => {
                setDragFromIndex(null);
                setDragOverIndex(null);
              } : undefined}
            >
              {dragFromIndex !== null && dragOverIndex === combinedIdx && dragFromIndex !== combinedIdx && (
                <DropMarker position={dragOverPosition} />
              )}
              {isDisabled && !isMobile && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}>
                  <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>利用されません</span>
                </div>
              )}
              <div style={{
                ...(isDisabled ? { opacity: 0.4, filter: 'grayscale(100%)' } : undefined),
                ...(dragFromIndex === combinedIdx ? { opacity: 0.4 } : undefined),
              }}>
                <AssetPreview
                  asset={item.asset}
                  flagged={facePrivacyFlagged}
                  sanitized={facePrivacySanitized}
                  sanitizedLabel={sanitizedStyleById.get(item.asset.id) === 'line_art' ? '線画化済み' : '覆面化済み'}
                  mentionBadge={mentionBadgeByAssetId.get(item.asset.id)}
                  onOpenLayerModal={onOpenLayerModal}
                  // カタログ由来の音声 (§2.2 source.kind='audio_catalog') は id が
                  // audio_catalog_items.id であり materials/temporary_materials の
                  // レコードではないため、素材詳細プレビュー (fetchTemporaryMaterialById /
                  // fetchCloudMaterialById) を呼んではいけない (誤って他素材を引く/
                  // 404を招く)。試聴は既存の再生ボタンで足りるためクリック自体を無効化する。
                  onClick={item.asset.sourceKind === 'audio_catalog' ? undefined : () => setPreviewAsset(item.asset)}
                  bottomControl={
                    // 参照動画は「動画 / 最初のフレーム / 最後のフレーム」を選ぶドロップダウン。
                    item.asset.usage === 'reference_video'
                      ? (() => {
                          const refMode = item.asset.videoReferenceMode ?? 'video';
                          const isExtracting = extractingFrameIds.has(item.asset.id);
                          return (
                            <button
                              type="button"
                              className={VIDEO_REF_MODE_TRIGGER_CLASS}
                              disabled={isExtracting}
                              aria-label="参考素材に使う部分を選択"
                              onClick={(e) => {
                                e.stopPropagation();
                                videoRefModeDropdown.open(item.asset.id, e.currentTarget);
                              }}
                              style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                padding: '2px 4px',
                                background: 'rgba(0, 0, 0, 0.65)',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 700,
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 3,
                                cursor: isExtracting ? 'default' : 'pointer',
                              }}
                            >
                              <span>{isExtracting ? '抽出中…' : VIDEO_REFERENCE_MODE_LABELS[refMode]}</span>
                              {!isExtracting && (
                                <svg width="7" height="5" viewBox="0 0 8 6" fill="currentColor" aria-hidden="true">
                                  <path d="M0 0l4 6 4-6z" />
                                </svg>
                              )}
                            </button>
                          );
                        })()
                      // 参照音声は用途 (声/BGM/効果音/サウンドロゴ) を選ぶドロップダウン。
                      // 再生ボタンが下部中央にあるため (AssetPreview 内)、上部に重ねる。
                      : item.asset.usage === 'reference_audio'
                      ? (() => {
                          const usage = item.asset.audioUsage;
                          // 短尺のためグレーアウトされているチップは実送信されない (buildVideoPayload
                          // が除外) ため、用途未選択の警告色・生成ブロック対象から外す (isDisabled は
                          // このチップが短尺除外/参照上限等で「利用されません」表示になっているか)。
                          const usageLabel = usage ? AUDIO_USAGE_LABELS[usage] : (isDisabled ? '用途未選択' : '用途を選択');
                          const binding = item.asset.audioVoiceBinding;
                          const bindingActor = binding ? actorItems.find((a) => a.id === binding.id) : undefined;
                          const bindingLabel = usage === 'voice'
                            ? (binding ? (bindingActor?.displayName ?? '声の相手') : '未指定(ナレーター)')
                            : null;
                          return (
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <button
                                type="button"
                                className={AUDIO_USAGE_TRIGGER_CLASS}
                                aria-label="参照音声の用途を選択"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  audioUsageDropdown.open(item.asset.id, e.currentTarget);
                                }}
                                style={{
                                  padding: '2px 4px',
                                  // 未選択は用途宣言必須の方針 (計画書 §2.1) に沿って警告色で目立たせる。
                                  // ただし短尺除外等でグレーアウト済みのチップは実送信されないため対象外。
                                  background: usage || isDisabled ? 'rgba(0, 0, 0, 0.65)' : 'rgba(217, 119, 6, 0.85)',
                                  color: '#fff',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  border: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 3,
                                  cursor: 'pointer',
                                }}
                              >
                                <span>{usageLabel}</span>
                                <svg width="7" height="5" viewBox="0 0 8 6" fill="currentColor" aria-hidden="true">
                                  <path d="M0 0l4 6 4-6z" />
                                </svg>
                              </button>
                              {bindingLabel && (
                                <button
                                  type="button"
                                  className={AUDIO_VOICE_BINDING_TRIGGER_CLASS}
                                  aria-label="声の相手を選択"
                                  title={AUDIO_VOICE_EXPECTATION_NOTE}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    audioVoiceBindingDropdown.open(item.asset.id, e.currentTarget);
                                  }}
                                  style={{
                                    padding: '1px 4px',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    color: 'rgba(255, 255, 255, 0.85)',
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 96 }}>{bindingLabel}</span>
                                  <svg width="6" height="4" viewBox="0 0 8 6" fill="currentColor" aria-hidden="true">
                                    <path d="M0 0l4 6 4-6z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })()
                      : facePrivacySanitizeAvailable
                      ? (
                          // 実在人物フィルタ対象の素材: カード上から静止画の覆面化 / 線画化を実行。
                          // 動画は従来どおり生成ボタンで再生成する (サニタイズ画像は自動差し替え)。
                          // 線画化は覆面化しても弾かれるケースの逃げ道 (画像全体を線画に変換)。
                          <div className={ddStyles.facePrivacyAssetBtnStack}>
                            <button
                              type="button"
                              className={ddStyles.facePrivacyAssetSanitizeBtn}
                              disabled={!!facePrivacySanitizingStyle}
                              title="この画像の顔をAIで消した画像を作成します。作成後、動画をもう一度生成してください"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleFacePrivacySanitize(item.asset, 'faceless');
                              }}
                            >
                              <VenetianMask size={11} aria-hidden="true" />
                              {facePrivacySanitizingStyle === 'faceless' ? '覆面化中…' : '覆面化する'}
                            </button>
                            <button
                              type="button"
                              className={ddStyles.facePrivacyAssetSanitizeBtn}
                              disabled={!!facePrivacySanitizingStyle}
                              title="この画像全体をAIでモノクロ線画に変換した画像を作成します (覆面化しても弾かれる場合に有効です)。作成後、動画をもう一度生成してください"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleFacePrivacySanitize(item.asset, 'line_art');
                              }}
                            >
                              <PenTool size={11} aria-hidden="true" />
                              {facePrivacySanitizingStyle === 'line_art' ? '線画化中…' : '線画にする'}
                            </button>
                          </div>
                        )
                      : undefined
                  }
                  thumbnailOverrideUrl={
                    // フレーム指定中は抽出済みフレーム画像をサムネイルに出す
                    item.asset.usage === 'reference_video' && (item.asset.videoReferenceMode === 'first_frame' || item.asset.videoReferenceMode === 'last_frame')
                      ? item.asset.videoFrameMaterials?.[item.asset.videoReferenceMode === 'first_frame' ? 'first' : 'last']?.url
                      : undefined
                  }
                  bottomLabel={
                    item.asset.usage !== 'reference_video' && frameModeOverlay && !isDisabled
                      ? (() => {
                          const short = isMobile || framePlaceholderAspectRatio === '9:16';
                          if (item.assetIdx === 0) return short ? '最初' : '最初のフレーム';
                          if (item.assetIdx === 1) return short ? '最後' : '最後のフレーム';
                          return undefined;
                        })()
                      : undefined
                  }
                  slotSize={
                    framePlaceholderAspectRatio && !isDisabled
                      ? (framePlaceholderAspectRatio === '9:16'
                          ? { width: Math.round(120 * 9 / 16), height: 120 }
                          : { width: Math.round(120 * 16 / 9), height: 120 })
                      : undefined
                  }
                />
              </div>
            </li>
          );
        });
      })()}
      {/* フレームモード時: 未選択スロットのプレースホルダー */}
      {framePlaceholderCount !== undefined && (() => {
        const missing = Math.max(0, framePlaceholderCount - assets.length);
        if (missing === 0) return null;
        return Array.from({ length: missing }).map((_, i) => {
          const slotIdx = assets.length + i;
          const isPortrait = framePlaceholderAspectRatio === '9:16';
          const short = isMobile || isPortrait;
          const label = slotIdx === 0 ? (short ? '最初' : '最初のフレーム') : slotIdx === 1 ? (short ? '最後' : '最後のフレーム') : '';
          const requirement = slotIdx === 0 ? '必須' : slotIdx === 1 ? '任意' : '';
          // 高さは既存アセット(120px)に合わせ、幅はアスペクト比から算出
          const rectH = 120;
          const rectW = isPortrait ? Math.round(120 * 9 / 16) : Math.round(120 * 16 / 9);
          return (
            <li key={`placeholder-${slotIdx}`} className={styles.assetCard} style={{ position: 'relative' }} draggable={false}>
              <div className={styles.assetPreviewContainer}>
                <div
                  style={{
                    width: rectW,
                    height: rectH,
                    background: 'rgba(245, 241, 230, 0.05)',
                    border: '1px dashed rgba(245, 241, 230, 0.35)',
                    borderRadius: 12,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {requirement && (
                    <span style={{
                      color: 'rgba(245, 241, 230, 0.35)',
                      fontSize: '11px',
                      fontWeight: 500,
                      pointerEvents: 'none',
                    }}>
                      {requirement}
                    </span>
                  )}
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: '2px 4px',
                    background: 'rgba(0, 0, 0, 0.65)',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 700,
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}>
                    {label}
                  </div>
                </div>
              </div>
            </li>
          );
        });
      })()}
      {/* アップロード中のインジケーター */}
      {isUploading && (
        <li className={styles.assetCard}>
          <div className={styles.assetPreviewContainer}>
            <div className={styles.assetPreview} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '0.75rem', color: '#888' }} title={isUploadingSvg ? SVG_LAYER_LOADING_LABEL : undefined}>
                {isUploadingSvg ? SVG_LAYER_LOADING_LABEL : '...'}
              </span>
            </div>
          </div>
        </li>
      )}
      {/* 商品画像の復元 (2026-10-22 利用者指示): 商品に紐づく画像素材のうち選択中素材から外れているものを戻す。
          素材の一番右に、目立たない暗いピルで置く */}
      {canRestoreProduct && (
        <li className={styles.restoreProductCard}>
          <button
            type="button"
            className={styles.restoreProductButton}
            onClick={restoreProductImages}
            title={`${brandConfig.labels.productShort}に紐づく画像のうち外れている${missingProductImageCount}枚を選択中素材に戻します`}
          >
            {brandConfig.labels.productShort}画像を復元
          </button>
        </li>
      )}
      {/* SEカタログから選ぶ (docs/features/reference-audio-roles-plan.md フェーズ5b・§2.7)。
          参照音声が使える文脈 (プロンプト入力モード + 参照モード + 対応モデル) でのみ表示する
          常設の入口。ベータ的機能のため目立たせない: 丸背景なしの小さな音符アイコンを
          margin-left:auto で右端へ寄せる (オーバーフロー時はスクロール末尾)。 */}
      {audioCatalogAvailable && (
        <li className={styles.audioCatalogEntry}>
          <button
            type="button"
            className={styles.audioCatalogEntryButton}
            onClick={() => setCatalogPickerOpen(true)}
            aria-label="SEカタログから選ぶ"
            title="SEカタログから選ぶ"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
        </li>
      )}
      {/* すべてクリアボタン（スクロール末尾、オーバーフロー時のみ） */}
      {onClearAll && hasOverflow && (
        <li className={styles.clearAllCard}>
          <button type="button" className={styles.clearAllCircle} onClick={onClearAll} aria-label="すべてクリア">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 5L17 17M17 5L5 17" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </button>
        </li>
      )}
      {/* 動画チップの参考素材モード選択メニュー (ポータル: スクロールコンテナのクリップ回避) */}
      {videoRefModeDropdown.openId && videoRefModeDropdown.pos && (() => {
        const targetAsset = assets.find((a) => a.id === videoRefModeDropdown.openId);
        if (!targetAsset) return null;
        const current = targetAsset.videoReferenceMode ?? 'video';
        return createPortal(
          <div
            ref={videoRefModeDropdown.menuRef}
            className={ddStyles.modelDropdownMenu}
            style={{ position: 'fixed', top: videoRefModeDropdown.pos.top, left: videoRefModeDropdown.pos.left }}
          >
            <div className={ddStyles.modelDropdownSectionHeader}>参考素材に使う部分</div>
            {VIDEO_REFERENCE_MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${ddStyles.modelDropdownItem} ${current === option.value ? ddStyles.modelDropdownItemActive : ''}`}
                onClick={() => { void handleSelectVideoRefMode(targetAsset, option.value); }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        );
      })()}
      {/* 参照音声チップの用途選択メニュー */}
      {audioUsageDropdown.openId && audioUsageDropdown.pos && (() => {
        const targetAsset = assets.find((a) => a.id === audioUsageDropdown.openId);
        if (!targetAsset) return null;
        const current = targetAsset.audioUsage;
        return createPortal(
          <div
            ref={audioUsageDropdown.menuRef}
            className={ddStyles.modelDropdownMenu}
            style={{ position: 'fixed', top: audioUsageDropdown.pos.top, left: audioUsageDropdown.pos.left }}
          >
            <div className={ddStyles.modelDropdownSectionHeader}>この音声の用途</div>
            {AUDIO_USAGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${ddStyles.modelDropdownItem} ${current === option.value ? ddStyles.modelDropdownItemActive : ''}`}
                onClick={() => handleSelectAudioUsage(targetAsset, option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        );
      })()}
      {/* 参照音声チップ (声用途) の人物バインド選択メニュー。actorItems は現在選択中の
          AI俳優 (casting) / Seedance プリセット俳優のみを候補にする (§2.3。実送信されるか
          どうかは生成時にバックエンドが確定するため、ここでは「選択可能な相手」を出すのみ)。 */}
      {audioVoiceBindingDropdown.openId && audioVoiceBindingDropdown.pos && (() => {
        const targetAsset = assets.find((a) => a.id === audioVoiceBindingDropdown.openId);
        if (!targetAsset) return null;
        const currentBindingId = targetAsset.audioVoiceBinding?.id ?? null;
        return createPortal(
          <div
            ref={audioVoiceBindingDropdown.menuRef}
            className={ddStyles.modelDropdownMenu}
            style={{ position: 'fixed', top: audioVoiceBindingDropdown.pos.top, left: audioVoiceBindingDropdown.pos.left }}
          >
            <div className={ddStyles.modelDropdownSectionHeader}>声の相手</div>
            <button
              type="button"
              className={`${ddStyles.modelDropdownItem} ${currentBindingId === null ? ddStyles.modelDropdownItemActive : ''}`}
              onClick={() => handleSelectAudioVoiceBinding(targetAsset, null)}
            >
              未指定(ナレーター)
            </button>
            {actorItems.map((actor) => {
              const bindingKind: AudioVoiceBindingKind = actor.kind === 'casting' ? 'actor' : 'seedance_preset';
              return (
                <button
                  key={actor.id}
                  type="button"
                  className={`${ddStyles.modelDropdownItem} ${currentBindingId === actor.id ? ddStyles.modelDropdownItemActive : ''}`}
                  onClick={() => handleSelectAudioVoiceBinding(targetAsset, { kind: bindingKind, id: actor.id })}
                >
                  {actor.displayName}
                </button>
              );
            })}
          </div>,
          document.body,
        );
      })()}
      {/* 素材プレビューモーダル */}
      {previewAsset && (
        <MaterialPreviewModal
          material={previewMaterial ?? toMaterialData(previewAsset)}
          materialId={previewAsset.id}
          onClose={() => setPreviewAsset(null)}
          isTemporary={previewMaterial?.folder_type
            ? previewMaterial.folder_type === 'temporary'
            : previewAsset.type === 'temporary'}
          showEditControls={false}
          showAddToSelectionButton={false}
        />
      )}
    </ul>
    <AudioCatalogPickerModal isOpen={catalogPickerOpen} onClose={() => setCatalogPickerOpen(false)} />
    </div>
  );
}
